import React, { useState, useRef } from 'react'
import {
  StandardButtonBrand,
  StandardButtonSecondary,
} from '../../../../ui_reusable/styles/Button.styles'
import { StandardCheckbox } from '../../../../ui_reusable/StandardInputs'
import CustomIcons from '../../../../ui_reusable/CustomIcons'
import {
  FooterPanel,
  FooterContainer,
  CheckboxGroupContainer,
  ActionSection,
  CheckboxContainer,
  IconWrapper,
  ButtonContainer,
} from '../../report_page_universal/styles/ReportFooter.styles'
import theme from '../../../../../styles/theme'
import { Progress, Tooltip, Whisper } from 'rsuite'
import useStandardToaster from '../../../../ui_reusable/StandardToaster'
import { ModelViewer, ModelComparer } from '../../../../../utils/ModelComparer'
import { useHasUnsavedChanges } from '../../report_page_universal/stores/baseReportStore'
import { useNavigate } from 'react-router-dom'
import { ROUTED_REPORT_MODES } from '../../../types/ReportTypes'
import type { SolutionReportStore } from '../types/solutionReportStoreTypes'
import type { UseBoundStore, StoreApi } from 'zustand'

// Flag to control whether hover tooltips (Whisper components) are enabled
const USE_COMPARER = false

const tooltipStyle = {
  maxWidth: 'none',
  width: 'auto',
  backgroundColor: 'rgba(255,255,255,0.7)',
  backdropFilter: 'blur(10px)',
  padding: '1rem',
  borderRadius: '0.7rem',
  boxShadow: '0 0 15px rgba(0,0,0,0.1)',
}

interface ReportFooterProps {
  origin?: string
  store: UseBoundStore<StoreApi<SolutionReportStore>> // Properly typed store hook
}

const ReportFooter: React.FC<ReportFooterProps> = ({
  origin = '/models',
  store,
}) => {
  // Local state for UI-only concerns
  const [progress, setProgress] = useState(0)
  const [formats, setFormats] = useState(['pdf'])
  const checkboxRefs = useRef<{ [key: string]: HTMLInputElement | null }>({})

  // Get store state and actions
  const exportStatus = store((state: SolutionReportStore) => state.exportStatus)
  const unsavedChangesPresent = useHasUnsavedChanges(store)
  const hasModelBeenSaved = store(
    (state: SolutionReportStore) => state.hasModelBeenSaved,
  )
  const savingState = store((state: SolutionReportStore) => state.savingState)
  const freshReportId = store(
    (state: SolutionReportStore) => state.freshReportId,
  )

  // Get values from store instead of props
  const mode =
    store((state: SolutionReportStore) => state.mode) ||
    ROUTED_REPORT_MODES.NEW_MODEL

  // Get data needed for tooltips
  const initialModelData = store(
    (state: SolutionReportStore) => state.initialModelData,
  )
  const crntOldModelData = store(
    (state: SolutionReportStore) => state.crntOldModelData,
  )
  const initialReportData = store(
    (state: SolutionReportStore) => state.anyReport,
  )
  const title = store((state: SolutionReportStore) => state.getTitle())

  // Get store actions using selectors to avoid unnecessary re-renders
  const handleExport = store((state: SolutionReportStore) => state.handleExport)
  const handleSaveModelAndReportBtn = store(
    (state: SolutionReportStore) => state.handleSaveModelAndReportBtn,
  )
  const handleSaveReportBtn = store(
    (state: SolutionReportStore) => state.handleSaveReportBtn,
  )

  // Access toast notifications and navigation
  const { pushToast } = useStandardToaster()
  const navigate = useNavigate()

  /**
   * Handle checkbox state changes for export format selection
   */
  const handleCheckboxChange =
    (format: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.checked) {
        setFormats([...formats, format])
      } else {
        setFormats(formats.filter((f) => f !== format))
      }
    }

  /**
   * Handle icon clicks to toggle the corresponding checkbox
   */
  const handleIconClick = (format: string) => () => {
    if (checkboxRefs.current[format]) {
      checkboxRefs.current[format]?.click()
    }
  }

  /**
   * Handle export by calling the store function with required parameters
   */
  const handleExportClick = async () => {
    await handleExport(formats, {
      pushToast,
      setProgress,
    })
  }

  /**
   * Handle the cancel button click - navigates back to origin
   */
  const handleCancel = () => {
    navigate(origin)
  }

  /**
   * Handle save model and report button click
   */
  const handleSaveModelAndReport = async () => {
    await handleSaveModelAndReportBtn(pushToast)
  }

  /**
   * Handle save report button click
   */
  const handleSaveReport = async () => {
    await handleSaveReportBtn(pushToast)
  }

  /**
   * Reusable Save Report button component with consistent styling and behavior
   */
  const SaveReportButton: React.FC<{ buttonText?: string }> = ({
    buttonText = 'Save report',
  }) => (
    <StandardButtonBrand
      onClick={handleSaveReport}
      disabled={savingState.report || !unsavedChangesPresent}
      style={{
        opacity: unsavedChangesPresent ? 1 : 0.6,
        cursor: unsavedChangesPresent ? 'pointer' : 'not-allowed',
      }}
    >
      {savingState.report ? 'Saving...' : buttonText}
    </StandardButtonBrand>
  )

  /**
   * Button layout for initial model creation with a single "Save model & report" button
   * Always uses ModelViewer (no comparison)
   */
  const MergedLayoutViewer: React.FC = () => {
    const saveButton = (
      <StandardButtonBrand
        onClick={handleSaveModelAndReport}
        disabled={savingState.model || savingState.report}
      >
        {savingState.model || savingState.report
          ? 'Saving...'
          : 'Save model & report'}
      </StandardButtonBrand>
    )

    return (
      <ButtonContainer>
        <StandardButtonSecondary
          onClick={handleCancel}
          disabled={savingState.model || savingState.report}
        >
          Cancel
        </StandardButtonSecondary>

        {/* Conditionally render Whisper component for model viewing tooltip */}
        {USE_COMPARER ? (
          <Whisper
            placement="topStart"
            trigger="hover"
            preventOverflow
            speaker={
              <Tooltip style={tooltipStyle}>
                <ModelViewer
                  model={{ ...(initialModelData as any), name: title }}
                  projectId={
                    initialModelData?.project_solution?.project?.id ||
                    initialReportData?.project_solution?.project?.id
                  }
                  solutionId={
                    initialModelData?.project_solution?.solution?.id ||
                    initialReportData?.project_solution?.solution?.id
                  }
                />
              </Tooltip>
            }
          >
            {saveButton}
          </Whisper>
        ) : (
          saveButton
        )}
      </ButtonContainer>
    )
  }

  /**
   * Button layout for model editing with a single "Save model & report" button
   * Uses ModelComparer or ModelViewer based on data availability
   */
  const MergedLayoutEdit: React.FC = () => {
    const saveButton = (
      <StandardButtonBrand
        onClick={handleSaveModelAndReport}
        disabled={savingState.model || savingState.report}
      >
        {savingState.model || savingState.report
          ? 'Saving...'
          : 'Save model & report'}
      </StandardButtonBrand>
    )

    return (
      <ButtonContainer>
        <StandardButtonSecondary
          onClick={handleCancel}
          disabled={savingState.model || savingState.report}
        >
          Cancel
        </StandardButtonSecondary>

        {/* Conditionally render Whisper component for model comparison/viewing tooltip */}
        {USE_COMPARER ? (
          crntOldModelData ? (
            <Whisper
              placement="topStart"
              trigger="hover"
              preventOverflow
              speaker={
                <Tooltip style={tooltipStyle}>
                  <ModelComparer
                    newModel={initialModelData as any}
                    oldModel={crntOldModelData as any}
                    projectId={
                      initialModelData?.project_solution?.project?.id ||
                      initialReportData?.project_solution?.project?.id
                    }
                    solutionId={
                      initialModelData?.project_solution?.solution?.id ||
                      initialReportData?.project_solution?.solution?.id
                    }
                  />
                </Tooltip>
              }
            >
              {saveButton}
            </Whisper>
          ) : (
            <Whisper
              placement="topStart"
              trigger="hover"
              preventOverflow
              speaker={
                <Tooltip style={tooltipStyle}>
                  <ModelViewer
                    model={{ ...(initialModelData as any), name: title }}
                    projectId={
                      initialModelData?.project_solution?.project?.id ||
                      initialReportData?.project_solution?.project?.id
                    }
                    solutionId={
                      initialModelData?.project_solution?.solution?.id ||
                      initialReportData?.project_solution?.solution?.id
                    }
                  />
                </Tooltip>
              }
            >
              {saveButton}
            </Whisper>
          )
        ) : (
          saveButton
        )}
      </ButtonContainer>
    )
  }

  /**
   * Button layout for report-only operations
   */
  const OneButtonLayout: React.FC = () => (
    <ButtonContainer>
      <StandardButtonSecondary
        onClick={handleCancel}
        disabled={savingState.report}
      >
        {unsavedChangesPresent ? 'Cancel' : 'Exit'}
      </StandardButtonSecondary>
      <SaveReportButton />
    </ButtonContainer>
  )

  /**
   * Render different button layouts based on the current mode
   */
  const renderActionButtons = () => {
    switch (mode) {
      case ROUTED_REPORT_MODES.NEW_MODEL:
        // For NEW_MODEL mode, start with merged save button (always viewer)
        // After first save (when we have freshReportId), switch to one-button layout
        return freshReportId ? <OneButtonLayout /> : <MergedLayoutViewer />

      case ROUTED_REPORT_MODES.VIEW_REPORT:
        return <OneButtonLayout />

      case ROUTED_REPORT_MODES.RERUN_MODEL:
        return <OneButtonLayout />

      case ROUTED_REPORT_MODES.EDIT_MODEL:
        // For EDIT_MODEL mode, use MergedLayoutEdit which has comparer/viewer logic
        return !hasModelBeenSaved ? <MergedLayoutEdit /> : <OneButtonLayout />

      default:
        // Legacy fallback for backward compatibility
        return (
          <StandardButtonBrand onClick={handleSaveModelAndReport}>
            Save to 'saved models'
          </StandardButtonBrand>
        )
    }
  }

  return (
    <FooterPanel>
      <FooterContainer>
        {/* Export format selection and button area */}
        <ActionSection>
          <CheckboxGroupContainer>
            {/* Format selection checkboxes with icons */}
            {[
              { format: 'docx', Icon: CustomIcons.faFileWord, color: 'blue' },
              {
                format: 'pptx',
                Icon: CustomIcons.faFilePowerpoint,
                color: 'orange',
              },
              { format: 'pdf', Icon: CustomIcons.faFilePdf, color: 'red' },
            ].map(({ format, Icon, color }) => (
              <CheckboxContainer key={format}>
                <IconWrapper
                  onClick={handleIconClick(format)}
                  style={{ cursor: 'pointer' }}
                >
                  <Icon size="large" color={color} solid={false} />
                </IconWrapper>
                <StandardCheckbox
                  size="small"
                  checked={formats.includes(format)}
                  onChange={handleCheckboxChange(format)}
                  color={theme.colors.brand}
                  ref={(el) => {
                    checkboxRefs.current[format] = el
                  }}
                />
              </CheckboxContainer>
            ))}
          </CheckboxGroupContainer>
          <StandardButtonBrand
            onClick={handleExportClick}
            disabled={progress > 0}
          >
            {progress > 0 ? `Exporting... ${progress}%` : 'Export'}
          </StandardButtonBrand>
        </ActionSection>

        {/* Progress and status messaging area */}
        <div style={{ width: '20rem' }}>
          {/* Show progress bar during export */}
          {progress > 0 && (
            <Progress
              strokeColor={theme.colors.brand}
              trailColor={theme.colors.primary}
              percent={progress}
              status="active"
              showInfo={false}
            />
          )}
          {/* Show success/error message after export */}
          {exportStatus.show && !progress && (
            <div
              style={{
                color: exportStatus.success ? 'white' : '#FFAAAA',
                backgroundColor: exportStatus.success
                  ? theme.colors.brand
                  : theme.colors.suplementRed,
                textAlign: 'center',
                fontWeight: 'bold',
                opacity: exportStatus.opacity,
                transition: 'opacity 1s ease-in-out',
              }}
            >
              {exportStatus.success
                ? 'Download completed successfully!'
                : 'Something went wrong with the download.'}
            </div>
          )}
        </div>

        {/* Save/cancel buttons - content determined by current mode */}
        {renderActionButtons()}
      </FooterContainer>
    </FooterPanel>
  )
}

export default ReportFooter
